import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

type BrowserType = 'chrome' | 'safari' | 'firefox' | 'edge' | 'samsung' | 'opera' | 'other';
type OSType = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other';

interface PWAInstallState {
  // Core state
  isInstallable: boolean;
  isStandalone: boolean;
  canPrompt: boolean;

  // Browser/OS detection
  browser: BrowserType;
  os: OSType;

  // Actions
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;

  // UI state
  showInstructions: boolean;
  setShowInstructions: (show: boolean) => void;
}

const DISMISSED_KEY = 'pwa_install_dismissed';

/**
 * Hook to manage PWA installation prompt across different browsers and platforms
 *
 * Features:
 * - Detects if app is running in standalone/PWA mode
 * - Handles beforeinstallprompt event (Chrome, Edge, Samsung Internet)
 * - Detects browser and OS type
 * - Provides install prompt function
 * - Manages user dismissal state
 * - Shows instructions for browsers without API support (iOS Safari, Firefox)
 *
 * Browser Support:
 * - Chrome/Edge/Samsung/Opera: Full API support with native prompt
 * - iOS Safari: Manual instructions (no API)
 * - Firefox: Manual instructions (limited support)
 */
export const usePWAInstall = (): PWAInstallState => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [browser, setBrowser] = useState<BrowserType>('other');
  const [os, setOS] = useState<OSType>('other');

  // Detect browser type
  const detectBrowser = useCallback((): BrowserType => {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('edg/')) return 'edge';
    if (ua.includes('samsungbrowser')) return 'samsung';
    if (ua.includes('opr/') || ua.includes('opera')) return 'opera';
    if (ua.includes('chrome')) return 'chrome';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
    if (ua.includes('firefox')) return 'firefox';

    return 'other';
  }, []);

  // Detect OS type
  const detectOS = useCallback((): OSType => {
    const ua = navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    if (/mac os x/.test(ua)) return 'macos';
    if (/windows/.test(ua)) return 'windows';
    if (/linux/.test(ua)) return 'linux';

    return 'other';
  }, []);

  // Check if app is running in standalone mode
  const checkStandaloneMode = useCallback((): boolean => {
    // Standard way - works on Chrome, Edge, Safari
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // iOS Safari specific
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    // Android TWA (Trusted Web Activity)
    if (document.referrer.includes('android-app://')) {
      return true;
    }

    return false;
  }, []);

  // Check if user previously dismissed the prompt
  const checkDismissed = useCallback((): boolean => {
    try {
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      return dismissed === 'true';
    } catch (error) {
      console.error('Failed to check dismissal state:', error);
      return false;
    }
  }, []);

  // Initialize detection
  useEffect(() => {
    setBrowser(detectBrowser());
    setOS(detectOS());
    setIsStandalone(checkStandaloneMode());
    setIsDismissed(checkDismissed());
  }, [detectBrowser, detectOS, checkStandaloneMode, checkDismissed]);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handler = (e: Event) => {
      // Prevent the default mini-infobar from appearing on mobile
      e.preventDefault();

      // Save the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      console.log('[PWA] beforeinstallprompt event fired - app is installable');
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // Listen for app installed event
  useEffect(() => {
    const handler = () => {
      console.log('[PWA] App was installed');
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('appinstalled', handler);

    return () => {
      window.removeEventListener('appinstalled', handler);
    };
  }, []);

  // Prompt user to install
  const promptInstall = useCallback(async (): Promise<boolean> => {
    // If we have a deferred prompt (Chrome, Edge, Samsung Internet, Opera)
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const choiceResult = await deferredPrompt.userChoice;

        if (choiceResult.outcome === 'accepted') {
          console.log('[PWA] User accepted the install prompt');
          setDeferredPrompt(null);
          return true;
        } else {
          console.log('[PWA] User dismissed the install prompt');
          return false;
        }
      } catch (error) {
        console.error('[PWA] Error showing install prompt:', error);
        return false;
      }
    }

    // For browsers without API support
    // - All browsers on iOS (Chrome, Edge, Firefox, etc. all use Safari's engine)
    // - Safari on all platforms
    // - Firefox on all platforms
    // Show instructions modal
    if (os === 'ios' || browser === 'safari' || browser === 'firefox') {
      setShowInstructions(true);
      return false;
    }

    console.log('[PWA] No install prompt available');
    return false;
  }, [deferredPrompt, browser, os]);

  // Dismiss the prompt (user clicked "Not now" or similar)
  const dismissPrompt = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
      setIsDismissed(true);
      console.log('[PWA] Install prompt dismissed by user');
    } catch (error) {
      console.error('[PWA] Failed to save dismissal state:', error);
    }
  }, []);

  // Determine if we should show the install button
  const isInstallable = !isStandalone && !isDismissed;

  // Determine if we can show a native prompt or instructions
  // - Native prompt: Chrome, Edge, Samsung Internet, Opera on Android/Desktop
  // - Instructions: Safari (all platforms), Firefox (all platforms), any browser on iOS
  const canPrompt = !!deferredPrompt || browser === 'safari' || browser === 'firefox' || os === 'ios';

  return {
    isInstallable,
    isStandalone,
    canPrompt,
    browser,
    os,
    promptInstall,
    dismissPrompt,
    showInstructions,
    setShowInstructions,
  };
};
